import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Loader2, CheckCircle, Eye, Link2, SlidersHorizontal, BarChart3,
    RefreshCw, Brain, Award, Sparkles, ChevronRight
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CH6_MODULES = [
    { id: 'registro_accion', name: 'Registro de acción visible', icon: Eye, color: '#5FAF9D' },
    { id: 'cadena_ejecucion', name: 'Cadena de ejecución', icon: Link2, color: '#E8B27A' },
    { id: 'escala_ajuste', name: 'Escala de ajuste', icon: SlidersHorizontal, color: '#7A9BA8' },
    { id: 'metricas_funcionales', name: 'Métricas funcionales', icon: BarChart3, color: '#3E5F6E' },
    { id: 'ciclo_progreso', name: 'Ciclo de progreso', icon: RefreshCw, color: '#5FAF9D' }
];

export default function ResumenCapitulo6() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally { setLoading(false); }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    const completedModules = profile?.modules_completed || [];
    const ch6Completed = CH6_MODULES.filter(m => completedModules.some(cm => cm.module_id === m.id));
    const completionRate = (ch6Completed.length / CH6_MODULES.length) * 100;
    const aiSummaries = profile?.ai_summaries || [];

    const registroSummary = aiSummaries.find(s => s.module === 'registro_accion');
    const cadenaSummary = aiSummaries.find(s => s.module === 'cadena_ejecucion');
    const escalaSummary = aiSummaries.find(s => s.module === 'escala_ajuste');
    const cicloSummary = aiSummaries.find(s => s.module === 'ciclo_progreso');

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                            <div>
                                <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']" data-testid="ch6-summary-title">Resumen Capítulo 6</h1>
                                <p className="text-sm text-[#4A5B63]">Sostenimiento y progreso</p>
                            </div>
                        </div>
                        <Link to="/usuario"><Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">Volver al inicio</Button></Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#5FAF9D] via-[#4A9A89] to-[#3E5F6E] rounded-3xl p-8 lg:p-12 mb-8 shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#E8B27A]/10 rounded-full blur-3xl" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-6 w-6 text-white" />
                                <span className="text-white font-medium uppercase tracking-wide text-sm">Capítulo 6 Completado</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-white font-['Outfit'] mb-4">Tu sistema de sostenimiento está activo</h2>
                            <p className="text-white/70 text-lg max-w-xl">Has construido un ciclo completo: acción visible, cadena diaria, escala flexible, métricas claras y ajuste continuo.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="relative w-40 h-40">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="12" />
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="#fff" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${completionRate * 4.4} 440`} className="transition-all duration-1000" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-bold text-white font-['Outfit']">{Math.round(completionRate)}%</span>
                                    <span className="text-white/50 text-sm">completado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Module Progress */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><RefreshCw className="h-5 w-5 text-[#5FAF9D]" />Progreso por módulo</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {CH6_MODULES.map((module) => {
                                    const isCompleted = completedModules.some(cm => cm.module_id === module.id);
                                    const Icon = module.icon;
                                    return (
                                        <div key={module.id} className="flex items-center gap-4" data-testid={`ch6-module-${module.id}`}>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'}`}>
                                                {isCompleted ? <CheckCircle className="h-5 w-5 text-white" /> : <Icon className="h-5 w-5 text-[#7A8A93]" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-[#1F2A30]' : 'text-[#7A8A93]'}`}>{module.name}</span>
                                                    <span className={`text-xs ${isCompleted ? 'text-[#5FAF9D]' : 'text-[#7A8A93]'}`}>{isCompleted ? '100%' : '0%'}</span>
                                                </div>
                                                <div className="h-2 bg-[#DCE3E8] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#5FAF9D] transition-all duration-500" style={{ width: isCompleted ? '100%' : '0%' }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Key Insights */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Sparkles className="h-5 w-5 text-[#E8B27A]" />Hallazgos clave</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {registroSummary?.claridad_accion && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1">Claridad de acción</p>
                                    <p className="text-sm text-[#1F2A30] font-medium">{registroSummary.claridad_accion}</p>
                                </div>
                            )}
                            {cadenaSummary?.puntuacion_consistencia && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-1">Consistencia de cadena</p>
                                    <p className="text-sm text-[#1F2A30] font-medium">{cadenaSummary.puntuacion_consistencia}/10</p>
                                </div>
                            )}
                            {escalaSummary?.flexibilidad && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#7A9BA8] uppercase mb-1">Flexibilidad</p>
                                    <p className="text-sm text-[#1F2A30] font-medium">{escalaSummary.flexibilidad}</p>
                                </div>
                            )}
                            {cicloSummary?.evolucion && (
                                <div className="p-3 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-xl border border-[#5FAF9D]/20">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1">Evolución del ciclo</p>
                                    <p className="text-sm text-[#1F2A30] font-medium">{cicloSummary.evolucion}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Final AI Insight */}
                {cicloSummary?.insight_usuario && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#5FAF9D]/20 rounded-full flex items-center justify-center shrink-0">
                                    <Brain className="h-5 w-5 text-[#5FAF9D]" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Reflexión final del capítulo</p>
                                    <p className="text-[#1F2A30] italic">"{cicloSummary.insight_usuario}"</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                <div className="text-center">
                    <Link to="/usuario">
                        <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="back-to-dashboard-ch6">
                            Volver al inicio <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
